import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useInsurerLogo } from '../hooks/useInsurerLogo';
import { analyticsService, DashboardData } from '../services/analyticsService';

import ProgramParticipationPanel from '../components/Analytics/ProgramParticipationPanel';
import RiskReadinessGauge from '../components/Analytics/RiskReadinessGauge';
import TopIncidentsChart from '../components/Analytics/TopIncidentsChart';
import BenchmarkingMap from '../components/Analytics/BenchmarkingMap';
import ThreatIncidentsChart from '../components/Analytics/ThreatIncidentsChart';
import TrainingCompletionChart from '../components/Analytics/TrainingCompletionChart';
import ClaimsPreventionPanel from '../components/Analytics/ClaimsPreventionPanel';
import ResilienceByIndustryChart from '../components/Analytics/ResilienceByIndustryChart';
import RetentionROIPanel from '../components/Analytics/RetentionROIPanel';
import ClaimsCorrelationPanel from '../components/Analytics/ClaimsCorrelationPanel';
import PartnerROIChartCard from '../components/Analytics/PartnerROIChartCard';

const AnalyticsDashboard: React.FC = () => {
  const { user } = useAuth();
  const { logoSrc, orgName } = useInsurerLogo();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hasAccess =
    user &&
    (user.is_superuser ||
      user.role === 'ADMIN' ||
      user.role === 'SUPER_ADMIN' ||
      user.role === 'INSURER');

  useEffect(() => {
    if (!hasAccess) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    analyticsService
      .getDashboard()
      .then((d) => {
        const demo = analyticsService.getDemoDashboard();
        setData({
          program_participation: d?.program_participation || demo.program_participation,
          risk_readiness: d?.risk_readiness || demo.risk_readiness,
          top_incidents: d?.top_incidents?.incidents?.length ? d.top_incidents : demo.top_incidents,
          benchmarking_map: d?.benchmarking_map?.states?.length ? d.benchmarking_map : demo.benchmarking_map,
          threat_incidents: d?.threat_incidents?.monthly_data?.length ? d.threat_incidents : demo.threat_incidents,
          training_completion: d?.training_completion?.by_industry?.length ? d.training_completion : demo.training_completion,
          claims_prevention: d?.claims_prevention || demo.claims_prevention,
          resilience_by_industry: d?.resilience_by_industry?.industries?.length ? d.resilience_by_industry : demo.resilience_by_industry,
          retention_roi: d?.retention_roi || demo.retention_roi,
        });
      })
      .catch(() => {
        // Fallback to demo data on error or offline
        setData(analyticsService.getDemoDashboard());
      })
      .finally(() => setIsLoading(false));
  }, [hasAccess]);

  if (!hasAccess) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#f8fafc]">
        <div className="p-8 w-full max-w-md text-center bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="mb-2 text-xl font-bold text-[#0c1e40]">Access Denied</h2>
          <p className="text-gray-600 text-sm">
            This dashboard is only available to administrators and insurers.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0c1e40]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 shadow p-8 text-center">
          <p className="text-sm text-red-800">{error || 'No analytics data available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 pb-16">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-center gap-6 pb-6 border-b border-gray-100 mb-8">
            <img
              src={logoSrc}
              alt={orgName || 'Insurance Partner'}
              className="h-14 sm:h-16 w-auto max-w-[240px] object-contain shrink-0"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (!target.src.endsWith('/insurance-partner-logo.svg')) {
                  target.src = '/insurance-partner-logo.svg';
                }
              }}
            />
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#0c1e40] tracking-tight">
              Resilience Dashboard for Insurers
            </h1>
          </div>

          {/* 4-Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 items-start">
            {/* Column 1 */}
            <div className="flex flex-col gap-6">
              <ProgramParticipationPanel data={data.program_participation} />
              <ThreatIncidentsChart data={data.threat_incidents} />
              <ResilienceByIndustryChart data={data.resilience_by_industry} />
            </div>

            {/* Column 2 */}
            <div className="flex flex-col gap-6">
              <RiskReadinessGauge data={data.risk_readiness} />
              <TrainingCompletionChart data={data.training_completion} />
              <ClaimsCorrelationPanel data={data.retention_roi} />
            </div>

            {/* Column 3 */}
            <div className="flex flex-col gap-6">
              <TopIncidentsChart data={data.top_incidents} />
              <ClaimsPreventionPanel data={data.claims_prevention} />
            </div>

            {/* Column 4 */}
            <div className="flex flex-col gap-6">
              <BenchmarkingMap data={data.benchmarking_map} />
              <RetentionROIPanel data={data.retention_roi} />
              <PartnerROIChartCard data={data.retention_roi} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
